(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktopQuery = window.matchMedia('(min-width: 1121px)');

  const focusable = (scope) => [...scope.querySelectorAll('a[href],button:not([disabled]),summary,input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((el) => !el.hidden && el.offsetParent !== null);

  function setCurrentLinks(scope = document) {
    const current = window.location.pathname.replace(/\/$/, '') || '/';
    scope.querySelectorAll('.v51-header a[href],.v51-footer a[href]').forEach((link) => {
      try {
        const path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/';
        if (path === current) link.setAttribute('aria-current', 'page');
      } catch (_) {}
    });
  }

  function initHeader(scope = document) {
    const header = scope.querySelector('[data-v51-header]') || document.querySelector('[data-v51-header]');
    if (!header || header.dataset.v51Bound) return;
    header.dataset.v51Bound = 'true';
    const nav = header.querySelector('[data-v51-nav]');
    const toggle = header.querySelector('[data-v51-nav-toggle]');
    const menus = [...header.querySelectorAll('[data-v51-menu]')];
    const scrim = header.querySelector('[data-v51-menu-scrim]');
    let returnFocus = null;

    const updateMenuTop = () => document.documentElement.style.setProperty('--v51-menu-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
    const updateOpenState = () => {
      const anyOpen = menus.some((menu) => menu.open);
      document.body.classList.toggle('is-v51-menu-open', anyOpen);
      if (scrim) scrim.hidden = !anyOpen;
      updateMenuTop();
    };
    const closeMenus = (except = null) => {
      menus.forEach((menu) => { if (menu !== except) menu.removeAttribute('open'); });
      updateOpenState();
    };
    const closeMobileNav = () => {
      nav?.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-v51-nav-open');
      closeMenus();
    };

    menus.forEach((menu) => {
      const summary = menu.querySelector(':scope > summary');
      summary?.addEventListener('click', () => {
        returnFocus = summary;
        requestAnimationFrame(() => {
          if (menu.open) closeMenus(menu); else updateOpenState();
        });
      });
      menu.addEventListener('toggle', () => {
        if (menu.open) closeMenus(menu);
        updateOpenState();
      });
    });

    toggle?.addEventListener('click', () => {
      const open = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('is-v51-nav-open', open);
      updateMenuTop();
      if (open) focusable(nav)[0]?.focus({ preventScroll: true });
    });

    scrim?.addEventListener('click', () => { closeMenus(); returnFocus?.focus({ preventScroll: true }); });
    document.addEventListener('click', (event) => { if (!header.contains(event.target)) closeMenus(); });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const hadMenu = menus.some((menu) => menu.open);
        closeMenus();
        if (document.body.classList.contains('is-v51-nav-open')) closeMobileNav();
        if (hadMenu) returnFocus?.focus({ preventScroll: true }); else toggle?.focus({ preventScroll: true });
      }
      if (event.key === 'Tab' && document.body.classList.contains('is-v51-nav-open') && !desktopQuery.matches) {
        const items = focusable(header);
        if (!items.length) return;
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });

    header.addEventListener('click', (event) => {
      if (!desktopQuery.matches && event.target.closest('a[href]')) closeMobileNav();
    });
    window.addEventListener('resize', () => { updateMenuTop(); if (desktopQuery.matches) closeMobileNav(); }, { passive: true });
    window.addEventListener('scroll', () => {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
      updateMenuTop();
    }, { passive: true });
    updateMenuTop();
  }

  function initReveals(scope = document) {
    if (!document.body.classList.contains('has-editorial-motion')) return;
    const elements = [...scope.querySelectorAll('[data-v51-reveal],.v5-pathway,.v5-capability-row,.v5-proof-pillar,.v5-resource-tool,.v51-insight-card,.v51-guide')];
    if (!elements.length) return;
    elements.forEach((element, index) => {
      element.classList.add('v51-reveal');
      element.style.setProperty('--v51-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
    });
    if (reducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }), { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    elements.forEach((element) => observer.observe(element));
  }

  function initProcessStages(scope = document) {
    const motionEnabled = document.body.classList.contains('has-editorial-motion');
    scope.querySelectorAll('[data-v51-process]').forEach((process) => {
      if (process.dataset.v51ProcessBound) return;
      process.dataset.v51ProcessBound = 'true';
      const steps = [...process.querySelectorAll('[data-v51-process-step]')];
      if (!steps.length) return;
      steps.forEach((step, index) => step.style.setProperty('--v51-stage-index', index));
      if (!motionEnabled || reducedMotion || !('IntersectionObserver' in window)) {
        process.classList.add('is-settled');
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry?.isIntersecting) return;
        process.classList.add('is-in-view');
        observer.disconnect();
      }, { threshold: 0.22, rootMargin: '0px 0px -12% 0px' });
      observer.observe(process);
    });
  }

  function initDecisionProgress() {
    if (reducedMotion || window.innerWidth < 1240 || document.querySelector('.v51-decision-progress')) return;
    const definitions = [
      ['Orient', '.v5-home-hero,.v3-hero'],
      ['Choose', '.v5-decision-pathways,.v51-resource-navigator'],
      ['Evaluate', '.v5-capability-index,.v51-crosswalk,.v51-insights-library'],
      ['Understand', '.v5-process-narrative,.v51-guide-library,.v51-process-section'],
      ['Act', '.v5-final-conversion,.v3-cta,.v51-footer__cta']
    ];
    const stages = definitions.map(([label, selector]) => [label, document.querySelector(selector)]).filter(([, element]) => element);
    if (stages.length < 3) return;
    const rail = document.createElement('nav');
    rail.className = 'v51-decision-progress';
    rail.setAttribute('aria-label', 'Page decision progress');
    rail.innerHTML = stages.map(([label], index) => `<button type="button" data-stage-index="${index}" aria-label="Jump to ${label}"><span></span><em>${label}</em></button>`).join('');
    document.body.appendChild(rail);
    rail.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-stage-index]');
      if (!button) return;
      stages[Number(button.dataset.stageIndex)][1].scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    const update = () => {
      const marker = window.scrollY + window.innerHeight * 0.42;
      let active = 0;
      stages.forEach(([, element], index) => { if (element.offsetTop <= marker) active = index; });
      rail.querySelectorAll('button').forEach((button, index) => {
        button.classList.toggle('is-active', index === active);
        button.classList.toggle('is-complete', index < active);
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function initFooterRoute(scope = document) {
    const route = scope.querySelector('[data-v51-footer-route]');
    if (!route || reducedMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      route.classList.add('is-active');
      observer.disconnect();
    }, { threshold: 0.55 });
    observer.observe(route);
  }

  function init(scope = document) {
    setCurrentLinks(scope);
    initHeader(scope);
    initReveals(scope);
    initProcessStages(scope);
    initFooterRoute(scope);
    if (scope === document) initDecisionProgress();
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
